import { useMemo, useState } from 'react';

/**
 * Permissive email shape check — matches RFC 5322's "addr-spec" loosely.
 * Server-side validation is authoritative; this is a quick local guard to
 * avoid showing the user a backend round-trip for obvious typos.
 *
 * Deliberately loose: accepts `+`/`-`/`_`/`.` in local-part, single `@`,
 * one or more domain labels, at least one TLD char. Does NOT enforce TLD
 * length, IDN normalization, or quoted-string local-parts.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmailShape(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

/**
 * Stateful hook for form email field. Tracks `value`, `touched` (blur fired),
 * and exposes `error` only when the field has been blurred AND is non-empty
 * AND fails the shape check. Empty + un-touched fields render no error.
 */
export function useEmailField(initialValue = '') {
  const [value, setValue] = useState(initialValue);
  const [touched, setTouched] = useState(false);

  const error = useMemo(() => {
    if (!touched) return null;
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    return isValidEmailShape(trimmed) ? null : 'Enter a valid email address.';
  }, [value, touched]);

  return {
    value,
    setValue,
    touched,
    onBlur: () => setTouched(true),
    error,
    isValid: isValidEmailShape(value.trim()),
  };
}
